"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { organizationsApi } from "@/lib/api/services/organizations.api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, MapPin, Users } from "lucide-react";
import Link from "next/link";

interface Organization {
  id: string;
  name: string;
  description: string;
  industry: string;
  location: string;
  memberCount: number;
  logoUrl?: string;
}

export default function OrganizationsPage() {
  const { data: organizations, isLoading } = useQuery({
    queryKey: ["organizations-list"],
    queryFn: async () => {
      const data = await organizationsApi.list();
      return ((data as unknown as { data?: Organization[] }).data ?? []) as Organization[];
    },
  });

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground">Organizations</h1>
          <p className="mt-1 text-sm text-muted-foreground">Discover HR organizations and companies.</p>
        </div>
        <Button asChild size="sm">
          <Link href="/contact">
            <Plus className="mr-2 h-4 w-4" /> Add Organization
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-lg bg-muted/10 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-3/4 bg-muted/10 rounded animate-pulse" />
                    <div className="h-3 w-1/2 bg-muted/10 rounded animate-pulse" />
                  </div>
                </div>
                <div className="h-3 w-full bg-muted/10 rounded animate-pulse mt-3" />
                <div className="h-3 w-2/3 bg-muted/10 rounded animate-pulse mt-2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : !organizations || organizations.length === 0 ? (
        <div className="py-20 text-center">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground/30" />
          <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">No organizations yet</h3>
          <p className="mt-2 text-sm text-muted-foreground">Be the first to add your organization.</p>
          <Button className="mt-6" size="sm" asChild>
            <Link href="/contact">Add Organization</Link>
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {organizations.map((org) => (
            <Link key={org.id} href={`/organizations/${org.id}`}>
              <Card className="group h-full hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Building2 className="h-6 w-6" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors truncate">
                        {org.name}
                      </h3>
                      <Badge variant="secondary" size="sm" className="mt-1">{org.industry}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2 mb-3">
                    {org.description}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {org.location}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {org.memberCount}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
